import { Component, OnInit } from '@angular/core';
import { FileViewerService } from '@ieCore/services/file-viewer.service';
import { StorageItem } from '@ieShared/interfaces/dynamic-form.interface';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { StorageService } from '@ieCore/services/storage.service';
import { StorageDirectory } from 'shared-general-libs/enum/storage';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { IeButtonComponent } from '@ieShared/components/ie-button/ie-button.component';
import { TranslateModule } from '@ngx-translate/core';
import { LoadingComponent } from '@ieShared/components/loading/loading.component';
import { NgxDocViewerModule } from 'ngx-doc-viewer';

@Component({
  selector: 'app-file-viewer',
  imports: [
    NzDrawerModule,
    NzButtonModule,
    PdfViewerModule,
    IeButtonComponent,
    TranslateModule,
    LoadingComponent,
    NgxDocViewerModule,
  ],
  standalone: true,
  templateUrl: './file-viewer.component.html',
  styleUrl: './file-viewer.component.scss',
})
export class FileViewerComponent implements OnInit {
  showFile = false;
  fileInfo: {
    file: StorageItem;
    storageDir: StorageDirectory;
  } | null = null;

  fileUrl: any = null;
  textContent: any = null;

  forceFileDownload = false;

  downloadInProgress = false;
  viewInProgress = false;

  zoom = 1;

  constructor(
    private fileViewerService: FileViewerService,
    private storageService: StorageService,
  ) {}

  ngOnInit(): void {
    this.fileViewerService.onProvideFile.subscribe((fileInfo) => {
      this.zoom = 1;
      this.forceFileDownload = false;
      if (fileInfo === null) {
        this.showFile = false;
        this.fileInfo = null;
        this.fileUrl = null;
        return;
      }

      this.showFile = true;
      this.fileInfo = fileInfo;

      this.viewInProgress = true;
      this.storageService
        .loadImageForPreview(fileInfo.storageDir, fileInfo.file.path)
        .subscribe({
          next: (blob: Blob) => {
            this.viewInProgress = false;
            this.fileUrl = URL.createObjectURL(blob);
            this.loadTextFile(blob);
          },
        });
    });
  }

  close(): void {
    this.fileViewerService.hideFile();
  }

  zoomIn() {
    this.zoom = this.zoom + 0.25;
  }

  zoomOut() {
    if (this.zoom === 0.5) {
      return;
    }
    this.zoom = this.zoom - 0.25;
  }

  downloadFile(): void {
    if (!this.fileInfo || this.forceFileDownload) {
      return;
    }

    this.downloadInProgress = true;
    this.storageService
      .loadImage(
        this.fileInfo.storageDir,
        this.fileInfo.file.path,
        this.fileInfo.file.originalFileName,
      )
      .subscribe({
        next: () => {
          this.downloadInProgress = false;
        },
        error: (error) => {
          console.error('Download failed:', error);
          this.downloadInProgress = false;
        },
      });
  }

  get fileName() {
    return this.fileInfo ? this.fileInfo.file.originalFileName : '';
  }

  get fileViewer():
    | 'pdf'
    | 'image'
    | 'doc'
    | 'txt'
    | 'html'
    | 'unknown'
    | null {
    if (!this.fileInfo) {
      return null;
    }

    if (this.fileInfo.file.contentType.startsWith('application/pdf')) {
      return 'pdf';
    }

    if (this.fileInfo.file.contentType.startsWith('image/')) {
      return 'image';
    }

    if (
      this.fileInfo.file.contentType.includes('plain') ||
      this.fileInfo.file.contentType.includes('csv')
    ) {
      return 'txt';
    }

    if (this.fileInfo.file.contentType.includes('html')) {
      return 'html';
    }

    if (
      this.fileInfo.file.contentType.startsWith(
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      )
    ) {
      return 'doc';
    }

    return 'unknown';
  }

  loadTextFile(blob: Blob) {
    const reader = new FileReader();
    reader.onload = () => {
      this.textContent = reader.result as string;
    };
    reader.readAsText(blob);
  }
}
